import { CanceledError } from 'axios';
import type { AuthIdentity } from '@/types/identity';
import type { AuthEndpoints, Credentials, SessionResult } from './endpoints/auth';

export type AuthStatus = 'unknown' | 'authenticating' | 'authenticated' | 'anonymous' | 'unavailable' | 'signout_failed';
export interface AuthSnapshot {
  status: AuthStatus;
  user: AuthIdentity | null;
  generation: number;
}
export type SessionEvent = 'invalidate' | 'signed-in';
export interface SessionCoordinator {
  lock<T>(work: () => Promise<T>): Promise<T>;
  publish?(event: SessionEvent): void;
  subscribe?(listener: (event: SessionEvent) => void): () => void;
}

export function createAuthSession(endpoints: AuthEndpoints, coordinator: SessionCoordinator) {
  let snapshot: AuthSnapshot = Object.freeze({ status: 'unknown', user: null, generation: 0 });
  let token: string | null = null;
  let controller = new AbortController();
  let refreshing: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach(listener => listener());
  const assertCurrent = (generation: number) => {
    if (generation !== snapshot.generation) throw new CanceledError('Session changed');
  };
  function clear(status: AuthStatus = 'anonymous') {
    controller.abort();
    controller = new AbortController();
    token = null;
    refreshing = null;
    snapshot = Object.freeze({ status, user: null, generation: snapshot.generation + 1 });
    emit();
  }
  function accept(result: SessionResult, generation: number) {
    assertCurrent(generation);
    const previous = snapshot.user;
    if (previous && (previous.id !== result.user.id || previous.actor_id !== result.user.actor_id ||
      previous.auth_version !== result.user.auth_version || previous.role_version !== result.user.role_version ||
      previous.role !== result.user.role || previous.scope !== result.user.scope ||
      previous.printing_room !== result.user.printing_room ||
      previous.must_change_password !== result.user.must_change_password ||
      JSON.stringify(previous.permissions) !== JSON.stringify(result.user.permissions))) {
      clear();
    }
    token = result.accessToken;
    const unchanged = previous && Object.keys(previous).length === Object.keys(result.user).length &&
      Object.entries(previous).every(([key, value]) => key === 'permissions'
        ? previous.permissions.length === result.user.permissions.length && previous.permissions.every((grant, index) => grant === result.user.permissions[index])
        : value === result.user[key as keyof AuthIdentity]);
    const user = unchanged ? previous : { ...result.user, permissions: [...result.user.permissions] };
    Object.freeze(user.permissions);
    snapshot = Object.freeze({ status: 'authenticated', user: Object.freeze(user), generation: snapshot.generation });
    emit();
  }
  async function refresh(generation = snapshot.generation, failedToken?: string) {
    assertCurrent(generation);
    if (failedToken && token && failedToken !== token) return;
    if (refreshing) return refreshing;
    const work = coordinator.lock(async () => {
      assertCurrent(generation);
      const result = await endpoints.refresh();
      accept(result, generation);
    }).catch(error => {
      if (generation === snapshot.generation) {
        clear(error?.statusCode === 401 ? 'anonymous' : 'unavailable');
      }
      throw error;
    });
    refreshing = work;
    try { await work; } finally { if (refreshing === work) refreshing = null; }
  }
  async function accountCommand(run: (access: string) => Promise<void>, endsCurrentSession: boolean) {
    const generation = snapshot.generation;
    try {
      await coordinator.lock(async () => {
        assertCurrent(generation);
        if (!token) throw new Error('Authentication required');
        await run(token);
        assertCurrent(generation);
        if (endsCurrentSession) { clear(); coordinator.publish?.('invalidate'); }
      });
    } catch (error) {
      const status = (error as { statusCode?: number })?.statusCode || 0;
      // A lost mutation acknowledgement may already have revoked this session.
      if (endsCurrentSession && generation === snapshot.generation && (status === 0 || status >= 500)) {
        clear('signout_failed'); coordinator.publish?.('invalidate');
      }
      throw error;
    }
  }
  const unsubscribe = coordinator.subscribe?.(event => clear(event === 'signed-in' ? 'unknown' : 'anonymous'));
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    accessToken: () => token,
    requestSignal: () => controller.signal,
    assertCurrent,
    clear,
    refresh,
    async bootstrap() {
      if (snapshot.status !== 'unknown') return;
      try { await refresh(); } catch { /* Status carries anonymous or unavailable; no retry loop. */ }
    },
    async login(credentials: Credentials) {
      clear('authenticating');
      const generation = snapshot.generation;
      coordinator.publish?.('invalidate');
      try {
        await coordinator.lock(async () => {
          assertCurrent(generation);
          accept(await endpoints.login(credentials), generation);
          coordinator.publish?.('signed-in');
        });
      } catch (error) {
        if (generation === snapshot.generation) clear();
        throw error;
      }
    },
    async logout() {
      clear();
      const generation = snapshot.generation;
      try {
        coordinator.publish?.('invalidate');
        await coordinator.lock(() => { assertCurrent(generation); return endpoints.logout(); });
      } catch (error) {
        if (generation === snapshot.generation) clear('signout_failed');
        throw error;
      }
    },
    async changePassword(current: string, next: string) {
      await accountCommand(access => endpoints.changePassword(access, current, next), true);
    },
    async logoutAll(current: string) { await accountCommand(access => endpoints.logoutAll(access, current), true); },
    async revokeSession(id: string, current: boolean) { await accountCommand(access => endpoints.revokeSession(access, id), current); },
    dispose() { unsubscribe?.(); clear(); listeners.clear(); },
  };
}

export type AuthSession = ReturnType<typeof createAuthSession>;
